import { useEffect, useRef, useState } from 'react';
import { Head } from '~/components/shared/Head';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { SIGN_IN, SIGN_UP, RECOVER_PASSWORD } from '~/services/auth';
import { publicClient } from '~/services';
import { useSession } from '../contexts/SessionProvider';
import { PasswordForm } from '../domain/auth/PasswordForm';
import { MagicLinkForm } from '../domain/auth/MagicLinkForm';
import { RecoverPasswordForm } from '../domain/auth/RecoverPasswordForm';
import { VerificationDialog } from '../domain/auth/VerificationDialog';
import Logo from '../shared/Logo';
import { TranslateButton } from '../shared/TranslateButton';
import { AnimatedBackground } from '../shared/AnimatedBackground';
import { ChangeTheme } from '../shared/Theme';
import GoogleAuthButton from '../auth/GoogleAuthButton';
import { useSearchParams } from 'react-router-dom';

type AuthMode = 'password' | 'magic_link';

const Login = () => {
  const [t] = useTranslation('login');
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>('');
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'sign_in' | 'sign_up'>('sign_in');
  const [authMode, setAuthMode] = useState<AuthMode>('password');
  const [error, setError] = useState<string>('');
  const [showRecoverPassword, setShowRecoverPassword] = useState(false);
  const [recoverPasswordSuccess, setRecoverPasswordSuccess] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralCodeFromUrl, setReferralCodeFromUrl] =
    useState<boolean>(false);
  const { createSession } = useSession();
  const ref = useRef<HTMLInputElement>(null);

  const [signIn, { loading: signInLoading }] = useMutation(SIGN_IN, {
    onCompleted: (data) => {
      if (data.response.hasError) {
        const message: string = data.response.message || '';
        if (message.toLowerCase().includes('requires verification')) {
          setShowVerificationDialog(true);
          setError('');
          return;
        }
        setError(data.response.message || t('error'));
        return;
      }
      createSession(data);
    },
    onError: () => {
      setError(t('error'));
    },
    client: publicClient,
  });

  const [signUp, { loading: signUpLoading }] = useMutation(SIGN_UP, {
    onCompleted: (data) => {
      if (data.response.hasError) {
        setError(data.response.message || t('error'));
        return;
      }
      setError('');
      setShowVerificationDialog(true);
    },
    onError: () => {
      setError(t('error'));
    },
    client: publicClient,
  });

  const [recoverPassword, { loading: recoverLoading }] = useMutation(
    RECOVER_PASSWORD,
    {
      onCompleted: (data) => {
        if (data.response.hasError) {
          setError(data.response.message || t('error'));
          return;
        }
        setError('');
        setRecoverPasswordSuccess(true);
      },
      onError: () => {
        setError(t('error'));
      },
      client: publicClient,
    },
  );

  useEffect(() => {
    if (ref.current && ref.current.focus) {
      ref.current.focus();
    }
  }, [activeTab, authMode]);

  useEffect(() => {
    // Extract referral code from URL query parameter 'ref'
    const refParam = searchParams.get('ref');
    if (refParam) {
      setReferralCode(refParam);
      // Switch to sign up mode if a referral code is present
      setActiveTab('sign_up');
      setReferralCodeFromUrl(true);
    }
  }, [searchParams]);

  const handlePasswordSubmit = async (
    password: string,
    confirmPassword?: string,
    isRecover?: boolean,
    referralCodeFromForm?: string,
  ) => {
    if (isRecover) {
      setShowRecoverPassword(true);
      return;
    }

    try {
      if (activeTab === 'sign_up') {
        if (password !== confirmPassword) {
          setError(t('password_mismatch'));
          return;
        }

        await signUp({
          variables: {
            data: {
              email,
              password,
              ...(referralCodeFromForm && {
                referral_code: referralCodeFromForm,
              }),
            },
          },
        });
      } else {
        await signIn({
          variables: {
            data: {
              email,
              password,
              strategy: 'PASSWORD',
            },
          },
        });
      }
    } catch (error) {
      setError(t('error'));
    }
  };

  const handleRecoverPassword = async (email: string) => {
    try {
      await recoverPassword({
        variables: {
          data: {
            email,
          },
        },
      });
    } catch (error) {
      setError(t('error'));
    }
  };

  return (
    <>
      <Head title={t('login')} />
      <div className="md:hero relative h-screen overflow-hidden p-4 md:p-0">
        <AnimatedBackground />

        {/* Theme toggle button - bottom left */}
        <div className="absolute bottom-4 left-4 z-20 flex flex-row items-start gap-2">
          <ChangeTheme />
          <TranslateButton />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center">
          <Logo className="mb-8 w-32" />
          <div className="mt-4 flex w-full justify-center px-4"></div>
          <div className="card bg-base-100 flex h-fit w-full flex-col shadow-xl md:h-[32rem] md:w-96">
            {/* Fixed Header */}
            <div className="flex w-full">
              <button
                className={classNames(
                  'btn font-poppins grow !rounded-b-none !rounded-t-md',
                  {
                    '!border-b-0': activeTab === 'sign_in',
                  },
                )}
                onClick={() => setActiveTab('sign_in')}
              >
                {t('sign_in')}
              </button>
              <button
                className={classNames(
                  'btn font-poppins grow !rounded-b-none !rounded-t-md',
                  {
                    '!border-b-0': activeTab === 'sign_up',
                  },
                )}
                onClick={() => setActiveTab('sign_up')}
              >
                {t('sign_up')}
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex flex-col items-center overflow-y-auto pb-8">
              <div className="flex w-full justify-center gap-4 p-4">
                <button
                  className={classNames('btn font-poppins rounded-full', {
                    'btn-primary': authMode === 'password',
                  })}
                  onClick={() => setAuthMode('password')}
                >
                  {t('use_password')}
                </button>
                <button
                  className={classNames('btn font-poppins rounded-full', {
                    'btn-primary': authMode === 'magic_link',
                  })}
                  onClick={() => setAuthMode('magic_link')}
                >
                  {t('use_magic_link')}
                </button>
              </div>
              {showRecoverPassword ? (
                <RecoverPasswordForm
                  onSubmit={handleRecoverPassword}
                  onCancel={() => setShowRecoverPassword(false)}
                  loading={recoverLoading}
                  error={error}
                  success={recoverPasswordSuccess}
                />
              ) : authMode === 'magic_link' ? (
                <MagicLinkForm
                  email={email}
                  onEmailChange={setEmail}
                  emailSent={emailSent}
                  setEmailSent={setEmailSent}
                  inputRef={ref}
                />
              ) : (
                <PasswordForm
                  email={email}
                  onEmailChange={setEmail}
                  onSubmit={handlePasswordSubmit}
                  mode={activeTab}
                  error={error}
                  loading={
                    activeTab === 'sign_up' ? signUpLoading : signInLoading
                  }
                  inputRef={ref}
                  referralCode={referralCode}
                  onReferralCodeChange={setReferralCode}
                  referralCodeFromUrl={referralCodeFromUrl}
                />
              )}

              {/* Google OAuth Button */}
              <div className="mb-4 w-full px-4">
                <div className="divider text-base-content/60 text-sm">
                  {t('or_divider')}
                </div>
                <GoogleAuthButton
                  text={activeTab === 'sign_up' ? 'signup_with' : 'signin_with'}
                  referralCode={referralCode}
                  onSuccess={() => {
                    setError('');
                    // Navigation handled by GoogleAuthButton
                  }}
                  onError={(errorMessage) => {
                    setError(errorMessage);
                  }}
                />
              </div>

              {(signInLoading || signUpLoading || recoverLoading) && (
                <progress className="progress progress-primary mt-12 w-full"></progress>
              )}
            </div>
          </div>
        </div>
      </div>
      {showVerificationDialog && (
        <VerificationDialog
          email={email}
          onClose={() => {
            setShowVerificationDialog(false);
            setActiveTab('sign_in');
            setEmail('');
          }}
        />
      )}
    </>
  );
};

export default Login;
